/* ==========================================================================
   Quick add — grid variant picker.
   Click a card's "+" → fetch product → add the only in-stock variant directly,
   or reveal a picker:
     • desktop (>=750px): inline panel anchored over the card image (Represent-style)
     • mobile (<750px): shared bottom sheet (thumb reach)
   After adding, hands off to <cart-drawer>. No new locale strings.
   ========================================================================== */

(function () {
  'use strict';

  const t = (window.theme && window.theme.t) || {};
  const desktop = () => window.matchMedia('(min-width: 750px)').matches;
  const storefrontRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
    || (window.theme && window.theme.routes && window.theme.routes.root)
    || '/';
  const publishCartEvent = (name, payload) => {
    if (typeof window.CustomEvent !== 'function') return;
    document.dispatchEvent(new window.CustomEvent('ev:commerce', {
      detail: { name, payload },
    }));
  };

  let sheet, titleEl, priceEl, variantsEl; // bottom sheet (mobile)
  let openInlineEl = null;                  // currently open inline panel (desktop)
  let hoverDismissed = null;                // card whose hover-open is suppressed until pointer leaves

  // After a click-toggle-close or Escape, suppress hover re-open for this card until
  // the pointer actually leaves it — otherwise bubbling mouseover instantly reopens.
  function markDismissed(card) {
    if (!card) return;
    hoverDismissed = card;
    card.addEventListener('mouseleave', function reset() {
      if (hoverDismissed === card) hoverDismissed = null;
    }, { once: true });
  }

  function variantButtonsHTML(product) {
    return product.variants.map((v) => `
      <button type="button" class="quick-add-sheet__variant" data-qa-variant="${v.id}"${v.available ? '' : ' disabled'}>
        ${v.title}
      </button>`).join('');
  }

  /* ---- bottom sheet (mobile) ---- */
  function buildSheet() {
    sheet = document.createElement('div');
    sheet.className = 'quick-add-sheet';
    sheet.setAttribute('data-quick-add-sheet', '');
    sheet.setAttribute('data-open', 'false');
    sheet.innerHTML = `
      <div class="quick-add-sheet__overlay" data-qa-close></div>
      <div class="quick-add-sheet__panel" role="dialog" aria-modal="true">
        <div class="quick-add-sheet__head">
          <div>
            <p class="quick-add-sheet__title" data-qa-title></p>
            <p class="quick-add-sheet__price text-mono" data-qa-price></p>
          </div>
          <button type="button" class="quick-add-sheet__close" data-qa-close aria-label="${t.close || 'Close'}">&times;</button>
        </div>
        <div class="quick-add-sheet__variants" data-qa-variants></div>
      </div>`;
    document.body.appendChild(sheet);
    titleEl = sheet.querySelector('[data-qa-title]');
    priceEl = sheet.querySelector('[data-qa-price]');
    variantsEl = sheet.querySelector('[data-qa-variants]');
  }

  function openSheet(product) {
    if (!sheet) buildSheet();
    const formatMoney = (window.theme && window.theme.formatMoney)
      || ((c) => new Intl.NumberFormat(window.theme?.locale || 'cs-CZ', { style: 'currency', currency: window.theme?.currency || 'CZK' }).format(c / 100));
    titleEl.textContent = product.title;
    priceEl.textContent = formatMoney(product.price);
    variantsEl.innerHTML = variantButtonsHTML(product);
    sheet.setAttribute('data-open', 'true');
    window.theme.lockScroll('quick-add');
  }

  function closeSheet() {
    if (!sheet) return;
    sheet.setAttribute('data-open', 'false');
    window.theme.unlockScroll('quick-add');
  }

  /* ---- inline panel (desktop) ---- */
  function openInline(card, product) {
    const panel = card.querySelector('[data-qa-inline]');
    if (!panel) return;
    closeInline();
    panel.innerHTML = variantButtonsHTML(product);
    panel.setAttribute('data-open', 'true');
    openInlineEl = panel;
    card.addEventListener('mouseleave', closeInline, { once: true });
  }

  function closeInline() {
    if (!openInlineEl) return;
    openInlineEl.removeAttribute('data-open');
    openInlineEl = null;
  }

  /* ---- add to cart ---- */
  async function addVariant(id, originBtn) {
    if (originBtn) originBtn.setAttribute('data-busy', '');
    const payload = { variant_id: id, quantity: 1, source: 'quick_add' };
    publishCartEvent('add_to_cart_attempt', payload);
    try {
      const res = await fetch(`${storefrontRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ id, quantity: 1 }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      publishCartEvent('add_to_cart_success', payload);
      closeSheet();
      closeInline();
      const cart = document.querySelector('cart-drawer');
      if (cart && typeof cart.refresh === 'function') {
        const page = document.querySelector('cart-page');
        await Promise.all([
          cart.refresh(),
          page && typeof page.refresh === 'function' ? page.refresh() : Promise.resolve(),
        ]);
        cart.open();
      } else {
        window.location.href = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (originBtn) originBtn.removeAttribute('data-busy');
    }
  }

  async function handleQuickAdd(btn) {
    const card = btn.closest('[data-product-handle]');
    const handle = card && card.getAttribute('data-product-handle');
    if (!handle) return;

    // Toggle: second click on the same card's open inline panel closes it.
    if (desktop() && openInlineEl && card.contains(openInlineEl)) {
      closeInline();
      markDismissed(card);
      return;
    }

    btn.setAttribute('data-busy', '');
    try {
      const product = await (await fetch(`${storefrontRoot()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })).json();
      const available = product.variants.filter((v) => v.available);
      if (product.variants.length === 1 || available.length === 1) {
        await addVariant((available[0] || product.variants[0]).id, null);
      } else if (desktop()) {
        openInline(card, product);
      } else {
        openSheet(product);
      }
    } catch (err) {
      console.error(err);
    } finally {
      btn.removeAttribute('data-busy');
    }
  }

  document.addEventListener('click', (e) => {
    const variantBtn = e.target.closest('[data-qa-variant]');
    if (variantBtn) {
      e.preventDefault();
      // Ignore repeat taps while a request for this button is in flight.
      if (!variantBtn.disabled && !variantBtn.hasAttribute('data-busy')) {
        addVariant(parseInt(variantBtn.dataset.qaVariant, 10), variantBtn);
      }
      return;
    }
    const addBtn = e.target.closest('[data-quick-add]');
    if (addBtn) {
      e.preventDefault();
      if (!addBtn.hasAttribute('data-busy')) handleQuickAdd(addBtn);
      return;
    }
    if (e.target.closest('[data-qa-close]')) {
      closeSheet();
      return;
    }
    // Click outside an open inline panel closes it.
    if (openInlineEl && !e.target.closest('[data-qa-inline]')) closeInline();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (openInlineEl) markDismissed(openInlineEl.closest('[data-product-handle]'));
      closeSheet();
      closeInline();
    }
  });

  /* ---- desktop hover: reveal the size picker without a click ----
     Pointer devices only (mouse, >=990px). Touch/mobile keeps click → bottom
     sheet untouched. Products cached so hovering doesn't re-fetch. Single-variant
     products are skipped — hover only reveals a size choice, it never adds. */
  const hoverCapable = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const productCache = new Map();

  function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const p = fetch(`${storefrontRoot()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .catch((err) => { productCache.delete(handle); throw err; });
    productCache.set(handle, p);
    return p;
  }

  document.addEventListener('mouseover', (e) => {
    if (!hoverCapable.matches) return;
    const addBtn = e.target.closest && e.target.closest('[data-quick-add]');
    if (!addBtn) return;
    const card = addBtn.closest('[data-product-handle]');
    if (!card) return;
    if (hoverDismissed === card) return;                    // suppressed until pointer leaves
    if (openInlineEl && card.contains(openInlineEl)) return; // already open for this card
    const handle = card.getAttribute('data-product-handle');
    Promise.resolve(fetchProduct(handle)).then((product) => {
      // The fetch is async — bail if the pointer already left the card, else a stray
      // panel opens on a card the cursor is no longer over (and wouldn't auto-close).
      if (!card.matches(':hover')) return;
      if (hoverDismissed === card) return;
      const available = product.variants.filter((v) => v.available);
      if (product.variants.length > 1 && available.length > 1) openInline(card, product);
    }).catch(() => {});
  });
})();
